import { useCallback } from 'react'
import {
  isDefined,
  type ConversationData,
  type ConversationFilter,
  type ConversationListInput,
  type ConversationListSuccess,
  type ConversationSortBy,
  type ConversationSortOrder,
} from '@terros/common'
import { useUserCache } from '../user'
import { useTantalimInfiniteQuery, type GetInfiniteHookResponse } from '../infiniteQuery'
import { conversationsInfiniteKey } from './keys'
import { useConversationCache } from './cache'

export type UseInfiniteConversationsInput = {
  reviewFlagged?: boolean
  filter?: ConversationFilter
  sortBy?: ConversationSortBy
  sortOrder?: ConversationSortOrder
}

export function useInfiniteConversations({
  filter,
  reviewFlagged,
  sortBy,
  sortOrder,
}: UseInfiniteConversationsInput = {}): GetInfiniteHookResponse<ConversationData> {
  const convoCache = useConversationCache()
  const userCache = useUserCache()

  const input: ConversationListInput = { filter, sortBy, sortOrder, reviewFlagged, version: 2 }
  const selectRecords = useCallback((page: ConversationListSuccess) => page.conversations, [])
  const handlePageFetched = useCallback(
    (result: ConversationListSuccess) => {
      result.conversations.forEach((c) => {
        convoCache.setItem(c)
        if (isDefined(c.owner)) userCache.setTinyUser(c.owner)
        if (isDefined(c.assignee)) userCache.setTinyUser(c.assignee)
      })
    },
    [convoCache, userCache]
  )

  return useTantalimInfiniteQuery<ConversationListInput, ConversationListSuccess, ConversationData>({
    url: '/conversation/list',
    queryKey: [...conversationsInfiniteKey(filter, sortBy, sortOrder), { reviewFlagged }],
    input,
    selectRecords,
    continueAfterEmptyPage: reviewFlagged,
    onPageFetched: handlePageFetched,
  })
}
